/**
*
*   Giveraway Form
*       - A Giveaway Signup Form
*
**/
'use client';

import Button from '@/components/button';
import Recaptcha from '@/libraries/recaptcha';
import Invite from '@/components/pages/giveaway/invite';
import Code from '@/components/code';
import { AnalyticTools } from '@/libraries/analytics';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useTranslations } from 'next-intl';
import { useReCaptcha } from 'next-recaptcha-v3';
import { useState } from 'react';


const Giveaway = {
	list:       'SFMmdh',
	discount:   'H0MVH6CCQ21Y',
	social:     {
		facebook:   'https://www.facebook.com/fernandpetal.ca/posts/pfbid04owxexJV8sG9G4miihf49YRTtoexQyK8gwhKbsNv9Rgs9N7iyvj3cXraX7ecxVuel',
		instagram:  'https://www.instagram.com/p/DB1TZiyTEyA/',
		pinterest:  'https://ca.pinterest.com/fernandpetal/',
		tiktok:     'https://www.tiktok.com/@fernandpetal/photo/7432319611606961414'
	}
};


//Build the Element
export default function GiveawayForm(){

	//The Form Settings
	const { control, register, getValues, setError, formState: { errors, isSubmitting, isSubmitSuccessful }, handleSubmit } = useForm();

	//Current Form State
	const [ status , setStatus ] = useState({
		result: 0,
		optedin: false
	});

	//Is the form loading
	const [ loading , setLoading ] = useState(false);

	//Has the Coupon Code been copied
	const [ copied , setCopied ] = useState(false);

	//The Coupon Code
	const [ code , setCode ] = useState('');

	//Page Translations
	const t = useTranslations();

	//Recaptcha
	const { executeRecaptcha } = useReCaptcha();

	//Form Settings
	const Form = {

		//Settings
		email: {
			validate: email => {
				return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email) || t('Forms.Errors.email.invalid'); //eslint-disable-line no-useless-escape
			}
		},

		//Error
		error: ({ message }) => <p className="error">{ message }</p> ,

		//A Thrown Error Message
		thrown: ( message ) => setError('root.serverError',{
			type: 500,
			message: `${ t('Forms.Errors.unknown')  } ${  message }`
		}),

		//On Submit
		submit: async data => {

			setLoading(true);

			const token = await executeRecaptcha('giveaway/signup');

			//Signup
			fetch( '/api/giveaway' , {
				method: 'PUT',
				body:   JSON.stringify( Object.assign({
					list:   Giveaway.list,
					token:  token
				},data) )
			})
			//Format to JSON
			.then( res => res.json() )

			//Success
			.then( response => {

				//Set Success or Failure
				setStatus( response );

				//An Error Occured
				if(!response.result )
					Form.thrown( JSON.stringify( response.error ) );

				//Track Analytics
				if( response.result )
					AnalyticTools.track(
						{ email: data.email },
						() => AnalyticTools.newsletter( 'giveaway' , data.email )
					);

				//Stop Loading
				setLoading(false);

			})

			//Catch Error
			.catch( err => {
				setLoading(false);
				Form.thrown( err.toString() );
			});
		}
	};


	return status.result > 0 ? (
		<>

			{/* Introduction */}
			<section id="introduction" className="container">
				<header>
					<h1>{ 'You\'re almost there!' }</h1>

					{/* Optin Required */}
					{ status.result == 2 && !status.optedin && (
						<p>{ 'You\'re already entered to win! Make sure you\'ve confirmed your entry into the giveaway by approving it in the email received. If you\'ve already confirmed it, you\'re in the contest to win 1 of 10 $50 gift cards! Why not get more entries by following the steps below!' }</p>
					)}
					{ status.result == 1 && !status.optedin && (
						<p>{ 'We\'ve sent you an email to confirm your giveaway entry. Once you confirm, you\'ll be entered to win 1 of 10 $50 gift cards.' }</p>
					)}

					{/* No Optin Required */}
					{ status.result == 2 && status.optedin && (
						<p>{ 'You\'re already in the contest to win 1 of 10 $50 gift cards! Why not get more entries by following the steps below!' }</p>
					)}
					{ status.result == 1 && status.optedin && (
						<p>{ 'You\'re entered to win 1 of 10 $50 gift cards!' }</p>
					)}
					<p>
					Winners will be drawn after the contest close date on December 1st. One person will be drawn every day from Dec 1st to 10th.
					</p>
					<p>
						<strong>
							<em>Want even more chances to win?</em> Follow us on social media and tag a friend on the posts below. Get <em>1 extra entry for each post that you do!</em>
						</strong>
					</p>
				</header>

				<div className="social">

					{/* Facebook */}
					<a href={ Giveaway.social.facebook } target="_blank">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
							<path d="M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256C0 376 82.7 476.8 194.2 504.5V334.2H141.4V256h52.8V222.3c0-87.1 39.4-127.5 125-127.5c16.2 0 44.2 3.2 55.7 6.4V172c-6-.6-16.5-1-29.6-1c-42 0-58.2 15.9-58.2 57.2V256h83.6l-14.4 78.2H287V510.1C413.8 494.8 512 386.9 512 256h0z"/>
						</svg>
					</a>

					{/* Instagram */}
					<a href={ Giveaway.social.instagram } target="_blank">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
							<path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
						</svg>
					</a>

					{/* Pinterest */}
					<a href={ Giveaway.social.pinterest } target="_blank">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512">
							<path d="M496 256c0 137-111 248-248 248-25.6 0-50.2-3.9-73.4-11.1 10.1-16.5 25.2-43.5 30.8-65 3-11.6 15.4-59 15.4-59 8.1 15.4 31.7 28.5 56.8 28.5 74.8 0 128.7-68.8 128.7-154.3 0-81.9-66.9-143.2-152.9-143.2-107 0-163.9 71.8-163.9 150.1 0 36.4 19.4 81.7 50.3 96.1 4.7 2.2 7.2 1.2 8.3-3.3 .8-3.4 5-20.3 6.9-28.1 .6-2.5 .3-4.7-1.7-7.1-10.1-12.5-18.3-35.3-18.3-56.6 0-54.7 41.4-107.6 112-107.6 60.9 0 103.6 41.5 103.6 100.9 0 67.1-33.9 113.6-78 113.6-24.3 0-42.6-20.1-36.7-44.8 7-29.5 20.5-61.3 20.5-82.6 0-19-10.2-34.9-31.4-34.9-24.9 0-44.9 25.7-44.9 60.2 0 22 7.4 36.8 7.4 36.8s-24.5 103.8-29 123.2c-5 21.4-3 51.6-.9 71.2C65.4 450.9 0 361.1 0 256 0 119 111 8 248 8s248 111 248 248z"/>
						</svg>
					</a>

					{/* TikTok */}
					<a href={ Giveaway.social.tiktok } target="_blank">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
							<path d="M448 209.9a210.1 210.1 0 0 1 -122.8-39.3V349.4A162.6 162.6 0 1 1 185 188.3V278.2a74.6 74.6 0 1 0 52.2 71.2V0l88 0a121.2 121.2 0 0 0 1.9 22.2h0A122.2 122.2 0 0 0 381 102.4a121.4 121.4 0 0 0 67 20.1z"/>
						</svg>
					</a>

					{/* Share Link */}
					<a target="_blank" className="share" onClick={()=>{

						//Copy to Clipboard
						navigator.clipboard.writeText( window.location.href );

						//Show Copied
						setCopied( true );

						//Hide it after
						window.setTimeout( () => setCopied(false) , 1000 );

					}}>
						<div className={ `success${   copied ? ' show' : '' }` }>
							{ 'Share Link Copied' }
						</div>
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
							<path d="M296 160c13.3 0 24-10.7 24-24l0-8 0-16 0-48L480 208 320 352l0-48 0-16 0-8c0-13.3-10.7-24-24-24l-8 0-96 0c-70.7 0-128 57.3-128 128c0 8.3 .7 16.1 2 23.2C47.9 383.7 32 350.1 32 304c0-79.5 64.5-144 144-144l112 0 8 0zm-8 144l0 16 0 32c0 12.6 7.4 24.1 19 29.2s25 3 34.4-5.4l160-144c6.7-6.1 10.6-14.7 10.6-23.8s-3.8-17.7-10.6-23.8l-160-144c-9.4-8.5-22.9-10.6-34.4-5.4s-19 16.6-19 29.2l0 32 0 16 0 16-32 0-80 0C78.8 128 0 206.8 0 304C0 417.3 81.5 467.9 100.2 478.1c2.5 1.4 5.3 1.9 8.1 1.9c10.9 0 19.7-8.9 19.7-19.7c0-7.5-4.3-14.4-9.8-19.5C108.8 431.9 96 414.4 96 384c0-53 43-96 96-96l64 0 32 0 0 16z"/>
						</svg>
					</a>
				</div>
			</section>

			{/* Discount Code */}
			<section id="code" className="container">
				<header>
					<h2>{ 'Don\'t want to wait?' }</h2>
					<p>
						{ 'Why wait! We\'ve created a single-use coupon you can use' } <strong><em>today</em></strong> { 'for 10% off! Don\'t wait tho - this offer expires in 24 hours!' } <em>Just click the coupon to get started!</em>
					</p>
				</header>
				<Code code={ Giveaway.discount } />

			</section>

			{/* Invite other people */}
			<Invite subscriber={ getValues('firstname') } />

		</>
	) : (
		<>
			{/* Introduction */}
			<section id="introduction" className="container">
				<header>
					<h1>
						Win a $50 Gift Card!
					</h1>
					<p>
						{ 'We\'re giving away 10 x $50 giftcards this holiday! The perfect amount to stock up essential oils, roll-ons, room sprays and bath products. Use it for stocking stuffers, or treat yourself this holiday season!' }
					</p>
				</header>
			</section>

			{/* Giveaway */}
			<section id="giveaway" className="container">
				<form onSubmit={ handleSubmit(Form.submit) }>

					{/* Submission Response Error */}
					{ errors.root?.serverError?.type > 200 && (
						<div className="failed">{
							errors.root.serverError.message
						}</div>
					) }

					{/* Row1 */}
					<div className="row">
						<div>
							<label htmlFor="name">{ t('Forms.Labels.firstname') }</label>
							<input name="name" placeholder={ `${ t('Forms.Labels.firstname')  }*` } {...register('firstname', { required: t('Forms.Errors.required',{ type: t('Forms.Labels.firstname') }) })} />
							<ErrorMessage errors={errors} name="firstname" render={ Form.error } />
						</div>
						<div>
							<label htmlFor="name">{ t('Forms.Labels.lastname') }</label>
							<input name="name" placeholder={ `${ t('Forms.Labels.lastname')  }*` } {...register('lastname', { required: t('Forms.Errors.required',{ type: t('Forms.Labels.firstname') }) })} />
							<ErrorMessage errors={errors} name="lastname" render={ Form.error } />
						</div>
					</div>

					{/* Row2 */}
					<div className="row">
						<div>
							<label htmlFor="email">{ t('Forms.Labels.email') }</label>
							<input name="email" placeholder={ `${ t('Forms.Labels.email')  }*` } {...register('email', { required:  t('Forms.Errors.required',{ type: t('Forms.Labels.email') }), validate: Form.email.validate })} />
							<ErrorMessage errors={errors} name="email" render={ Form.error } />
						</div>
						<div>
							<label htmlFor="country">{ t('Forms.Labels.country') }</label>
							<select
								name="country"
								{...register('country', {
									required: t('Forms.Errors.required',{ type: t('Forms.Labels.country') })
								})}>
								<option value="" disabled>Select a Country*</option>
								<option value="CA">Canada</option>
								<option value="US">United States</option>
							</select>
							<ErrorMessage errors={errors} name="country" render={ Form.error } />
						</div>
					</div>

					{/* Submit */}
					<div className="row">
						<div>
							<Button disabled={ isSubmitting || loading } button type="submit">{ t('Forms.Labels.enter') }</Button>
						</div>
					</div>

					{/* Footer */}
					<footer>

						{/* Recaptcha Message */}
						<p>{ Recaptcha.message }</p>

						<p className="contest-details">
							Contest closes Dec 1st, 2024. Contest open to all residents of the United States & Canada.
						</p>

					</footer>

				</form>
			</section>
		</>
	);

}